import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MANE_LOGO } from '../mane-logo';
import { Link, useParams, Navigate } from 'react-router-dom';
import { REVIEWS } from '../data/reviews';
import ManeLogo from '../components/Logo';
import api from '../api';

const BG      = '#050810';
const CARD    = '#0a0f1e';
const BORDER  = 'rgba(255,255,255,0.06)';
const GOLD    = '#D4AF37';
const OFF     = '#A0AABB';
const WHITE   = '#FFFFFF';

const DEFAULT_PROFILE = {
  displayName: 'Christian G Flair',
  title: 'Master Barber',
  location: 'Royal Palm Beach, FL',
  address: '1017 N SR-7 Suite 122, Royal Palm Beach, FL',
  phone: '5618885567',
  bio: '',
  years: '8+',
  clientCount: '1,000+',
  reviewScore: '5.0',
  reviewCount: '148+',
  instagram: 'christiangflair',
  tiktok: '',
  facebook: '',
  accentColor: '#D4AF37',
  ctaHeadline: 'Ready for the freshest cut of your life?',
  ctaSubtext: 'Book with Christian. Card on file. Confirmed in minutes.',
  hoursText: 'Mon–Sat 8AM–11PM · Sun 6PM–11PM',
  portfolio: [],
};

function fmtPhone(raw) {
  const d = (raw || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return raw;
}
function fmtPrice(svc) {
  const base = `$${parseFloat(svc.price).toFixed(0)}`;
  return svc.starting_price ? `from ${base}` : base;
}
function fmtAfterHours(svc) {
  if (!svc.after_hours_price) return null;
  return `$${parseFloat(svc.after_hours_price).toFixed(0)} after hrs`;
}
function fmtDuration(min) {
  if (!min) return '';
  if (min >= 60) { const h = Math.floor(min/60); const m = min%60; return m ? `${h}h ${m}m` : `${h}h`; }
  return `${min} min`;
}

const DEFAULT_SERVICES = [
  { id:1, name:"Precision Cut",         price:35, duration_minutes:30, badge:"POPULAR",       description:"Classic fade, taper, or crop with clean line-up" },
  { id:2, name:"Cut + Beard",           price:50, duration_minutes:45, badge:"BEST VALUE",    description:"Full cut service with beard trim & shape-up" },
  { id:3, name:"Full Experience",       price:75, duration_minutes:60, badge:"PREMIUM",       description:"Cut, beard, hot towel, scalp massage" },
  { id:4, name:"Kids Cut",              price:25, duration_minutes:25, badge:null,            description:"Ages 12 and under" },
  { id:5, name:"Line-Up Only",          price:20, duration_minutes:15, badge:null,            description:"Edge-up and line without full cut" },
  { id:6, name:"Beard Trim",            price:20, duration_minutes:15, badge:null,            description:"Shape, define, and clean beard" },
  { id:7, name:"After Hours Cut",       price:65, duration_minutes:30, badge:null,            description:"Late night premium slots" },
];

const BADGE_COLORS = {
  'POPULAR':          '#D4AF37',
  'BEST VALUE':       '#10b981',
  'PREMIUM':          '#a855f7',
  'FULL EXPERIENCE':  '#a855f7',
  'QUICK ADD-ON':     '#f59e0b',
  'MOBILE':           '#f59e0b',
};

function makeCSS(a) {
  return `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  .bp * { box-sizing:border-box; margin:0; padding:0; }
  .bp { font-family:'Inter',-apple-system,sans-serif; color:${WHITE}; background:${BG}; min-height:100vh; padding-bottom:80px; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes goldRingPulse { 0%,100%{box-shadow:0 0 16px rgba(212,175,55,0.3)} 50%{box-shadow:0 0 28px rgba(212,175,55,0.5)} }
  @keyframes ctaGlow { 0%,100%{box-shadow:0 0 20px rgba(10,8,4,0.6),0 4px 20px rgba(0,0,0,0.5)} 50%{box-shadow:0 0 32px rgba(212,175,55,0.25),0 6px 28px rgba(0,0,0,0.6)} }

  /* Sticky header */
  .bp-header { position:fixed; top:0; left:0; right:0; z-index:100; display:flex; align-items:center; justify-content:space-between; padding:0 16px; height:52px; background:rgba(5,8,16,0.95); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); border-bottom:1px solid ${BORDER}; }
  .bp-header-back { background:none; border:none; color:${WHITE}; font-size:22px; cursor:pointer; padding:4px 8px; display:flex; align-items:center; }
  .bp-header-name { color:${WHITE}; font-weight:700; font-size:15px; letter-spacing:0.01em; }
  .bp-header-share { background:none; border:none; color:${WHITE}; font-size:20px; cursor:pointer; padding:4px 8px; display:flex; align-items:center; }

  /* Profile section */
  .bp-profile { padding:72px 20px 0; max-width:600px; margin:0 auto; text-align:center; display:flex; flex-direction:column; align-items:center; }
  .bp-avatar-wrap { width:134px; height:134px; border-radius:30px; margin:0 auto 20px; border: 3px solid #D4AF37; box-shadow: 0 0 20px rgba(212,175,55,0.4); overflow:hidden; }
  .bp-avatar-inner { width:100%; height:100%; border-radius:24px; overflow:hidden; background:#050810; }
  .bp-avatar-inner img { width:100%; height:100%; object-fit:cover; border-radius:22px; display:block; }

  /* Name + title centered */
  .bp-display-name { font-size:clamp(22px,5vw,36px); font-weight:800; color:${WHITE}; letter-spacing:-0.02em; line-height:1.2; }
  .bp-title { font-size:14px; color:${GOLD}; font-weight:600; margin-top:4px; letter-spacing:0.06em; text-transform:uppercase; }
  .bp-founder-badge { display:inline-flex; align-items:center; gap:4px; margin-top:6px; font-size:11px; font-weight:800; color:${GOLD}; letter-spacing:0.06em; }

  /* Stats bar */
  .bp-stats-bar { display:flex; align-items:center; justify-content:center; gap:0; margin-top:16px; flex-wrap:wrap; }
  .bp-stats-bar span { font-size:12px; color:${GOLD}; font-weight:600; letter-spacing:0.02em; white-space:nowrap; }
  .bp-stats-sep { color:${GOLD}44; margin:0 10px; font-size:12px; user-select:none; }

  .bp-bio { font-size:14px; color:${OFF}; margin-top:12px; line-height:1.6; max-width:420px; margin-left:auto; margin-right:auto; }
  .bp-ig-link { margin-top:8px; }

  /* Book button — premium dark gold gradient */
  .bp-book-main { display:block; width:100%; max-width:400px; margin:28px auto 0; padding:18px 0; background:linear-gradient(135deg, #1a2a5e 0%, #2B7FFF 50%, #1a2a5e 100%); color:#ffffff; font-size:15px; font-weight:900; text-align:center; border-radius:14px; text-decoration:none; letter-spacing:0.15em; border:1px solid rgba(255,255,255,0.2); box-shadow:0 4px 24px rgba(43,127,255,0.4), 0 0 0 1px rgba(43,127,255,0.3); transition:transform 0.15s, box-shadow 0.15s; cursor:pointer; -webkit-tap-highlight-color:transparent; touch-action:manipulation; }
  .bp-book-main:hover, .bp-book-main:active { transform:translateY(-2px) scale(1.02); box-shadow:0 8px 32px rgba(43,127,255,0.6), 0 0 0 1px rgba(43,127,255,0.5); }

  /* Gold divider */
  .bp-gold-divider { height:1px; background:linear-gradient(90deg, transparent, ${GOLD}33, transparent); margin:32px 20px 0; max-width:600px; margin-left:auto; margin-right:auto; }

  /* Service cards — vertical list */
  .bp-svc-list { max-width:600px; margin:0 auto; padding:0 20px; }
  .bp-svc-card { display:flex; align-items:center; justify-content:space-between; padding:18px 0; text-decoration:none; border-bottom:1px solid ${GOLD}15; transition:background 0.15s; position:relative; }
  .bp-svc-card:last-child { border-bottom:none; }
  .bp-svc-card:hover { background:rgba(212,175,55,0.03); }
  .bp-svc-left { display:flex; flex-direction:column; gap:4px; flex:1; min-width:0; }
  .bp-svc-name-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .bp-svc-name { font-size:15px; font-weight:700; color:${WHITE}; }
  .bp-svc-dur { font-size:12px; color:#6B7280; font-weight:500; }
  .bp-svc-desc { font-size:12px; color:#6B7280; margin-top:2px; }
  .bp-svc-right { display:flex; align-items:center; gap:12px; flex-shrink:0; margin-left:16px; }
  .bp-svc-price { font-size:18px; font-weight:800; color:${GOLD}; }
  .bp-svc-chevron { color:${GOLD}44; font-size:18px; }
  .bp-svc-badge { font-size:8px; font-weight:800; padding:2px 6px; border-radius:4px; letter-spacing:0.06em; text-transform:uppercase; white-space:nowrap; }

  /* Reviews */
  .bp-rev-section { max-width:600px; margin:0 auto; padding:0 20px; }
  .bp-rev-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
  .bp-rev-card { background:${CARD}; border:1px solid ${BORDER}; border-radius:12px; padding:20px; transition:border-color 0.2s; }
  .bp-rev-card:hover { border-color:${GOLD}22; }

  /* Sticky bottom bar */
  .bp-bottom-bar { position:fixed; bottom:0; left:0; right:0; z-index:100; display:flex; align-items:center; justify-content:space-between; padding:10px 16px; background:rgba(5,8,16,0.97); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); border-top:1px solid ${BORDER}; }
  .bp-bottom-name { color:${WHITE}; font-size:14px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; flex:1; margin-right:12px; }
  .bp-bottom-book { background:linear-gradient(135deg, #1a2a5e 0%, #2B7FFF 50%, #1a2a5e 100%); color:#ffffff; padding:14px 28px; border-radius:10px; font-size:14px; font-weight:900; text-decoration:none; letter-spacing:0.1em; white-space:nowrap; transition:transform 0.15s, box-shadow 0.15s; flex-shrink:0; border:1px solid rgba(255,255,255,0.2); box-shadow:0 4px 16px rgba(43,127,255,0.4); touch-action:manipulation; -webkit-tap-highlight-color:transparent; }
  .bp-bottom-book:hover { transform:translateY(-1px) scale(1.02); border-color:${GOLD}66; }

  /* Section titles */
  .bp-section-title { font-size:10px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:${GOLD}; margin-bottom:14px; }

  /* Policy */
  .bp-policy-item { display:flex; align-items:center; gap:14px; padding:12px 16px; border-radius:10px; margin-bottom:6px; }

  /* Footer */
  .bp-footer { border-top:1px solid ${BORDER}; padding:22px 24px 90px; text-align:center; }

  .bp-social-link { color:${GOLD}; font-size:13px; font-weight:700; text-decoration:none; transition:color 0.2s; display:inline-block; }
  .bp-social-link:hover { color:${WHITE}; }

  .bp-powered { color:#5A6270; font-size:12px; text-decoration:none; transition:color 0.2s; }
  .bp-powered:hover { color:${GOLD}; }
  .bp-powered span { font-weight:700; }

  /* Track for review carousel */
  .bp-track { display:flex; gap:14px; overflow-x:auto; scroll-snap-type:x mandatory; padding-bottom:2px; }
  .bp-track::-webkit-scrollbar { display:none; }
  .bp-track { -ms-overflow-style:none; scrollbar-width:none; }
  .bp-rev-scroll { flex-shrink:0; width:clamp(280px,75vw,380px); scroll-snap-align:start; background:${CARD}; border:1px solid ${BORDER}; border-radius:12px; padding:20px; transition:border-color 0.2s; }
  .bp-rev-scroll:hover { border-color:${GOLD}22; }

  .bp-dot { height:6px; border-radius:3px; border:none; cursor:pointer; transition:width 0.25s, background 0.25s; padding:0; outline:none; }

  @media (min-width:480px) {
    .bp-avatar-wrap { width:150px; height:150px; border-radius:34px; }
    .bp-display-name { font-size:34px; }
  }
  @media (min-width:768px) {
    .bp-avatar-wrap { width:170px; height:170px; border-radius:38px; }
    .bp-display-name { font-size:38px; }
  }
  `;
}

export default function BarberProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = React.useState(DEFAULT_PROFILE);
  const [services, setServices] = React.useState([]);
  const [revIdx, setRevIdx] = React.useState(0);
  const [slugMismatch, setSlugMismatch] = React.useState(null);
  const trackRef = React.useRef(null);
  const timerRef = React.useRef(null);

  const sectionRefs = {
    services: React.useRef(null),
    reviews: React.useRef(null),
    location: React.useRef(null),
    hours: React.useRef(null),
  };

  React.useEffect(() => {
    fetch('/api/settings/public').then(r => r.json()).then(data => {
      if (data.profile) {
        try {
          const p = { ...DEFAULT_PROFILE, ...JSON.parse(data.profile) };
          setProfile(p);
          if (slug && p.businessSlug && slug.toLowerCase() !== p.businessSlug.toLowerCase()) {
            setSlugMismatch(p.businessSlug);
          }
        } catch {}
      }
    }).catch(() => {});
    fetch('/api/services').then(r => r.json()).then(d => { if (Array.isArray(d)) setServices(d); }).catch(() => {});
  }, [slug]);

  const goRev = React.useCallback((i) => {
    const idx = Math.max(0, Math.min(i, REVIEWS.length - 1));
    const t = trackRef.current;
    if (t?.children[idx]) t.scrollTo({ left: t.children[idx].offsetLeft, behavior: 'smooth' });
    setRevIdx(idx);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setRevIdx(prev => {
      const next = prev >= REVIEWS.length - 1 ? 0 : prev + 1;
      const t = trackRef.current;
      if (t?.children[next]) t.scrollTo({ left: t.children[next].offsetLeft, behavior: 'smooth' });
      return next;
    }), 5000);
  }, []);

  React.useEffect(() => {
    timerRef.current = setInterval(() => setRevIdx(prev => {
      const next = prev >= REVIEWS.length - 1 ? 0 : prev + 1;
      const t = trackRef.current;
      if (t?.children[next]) t.scrollTo({ left: t.children[next].offsetLeft, behavior: 'smooth' });
      return next;
    }), 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  if (slugMismatch) return <Navigate to={`/${slugMismatch}`} replace />;

  const a  = profile.accentColor || GOLD;
  const phone = fmtPhone(profile.phone);
  const svcs = services.length > 0 ? services : DEFAULT_SERVICES;
  const canonicalSlug = profile.businessSlug || slug || 'barber';
  const pageTitle = `${profile.displayName} — ${profile.title} in ${profile.location} | Mane`;
  const pageDesc = `Book with ${profile.displayName}, ${profile.title} in ${profile.location}. ${profile.reviewScore}★ rated, ${profile.reviewCount} reviews. Book online in seconds — card on file, confirmed instantly.`;
  const isFounder = profile.professional_id === 1 || profile.plan === 'founding';
  const initials = profile.displayName.split(' ').filter(w => w.length > 0).map(w => w[0]).slice(0,2).join('');

  return (
    <div className="bp">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={`https://joinmane.com/${canonicalSlug}`} />
        <meta property="og:image" content={`https://joinmane.com/og-christiangflair.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`https://joinmane.com/og-christiangflair.png`} />
        <meta property="og:type" content="profile" />
        <meta property="og:locale" content="en_US" />
        <meta name="keywords" content="barber Royal Palm Beach, barber near me, fade haircut West Palm Beach, men's haircut Royal Palm Beach FL, barber suite Royal Palm Beach, skin fade, taper fade, beard trim, Christian G Flair, Mane booking" />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Royal Palm Beach, FL" />
        <link rel="canonical" href={`https://joinmane.com/${canonicalSlug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BarberShop",
          "name":profile.displayName,"url":`https://joinmane.com/${canonicalSlug}`,
          "telephone":profile.phone,
          "address":{"@type":"PostalAddress","streetAddress":"1017 N SR-7 Suite 122","addressLocality":"Royal Palm Beach","addressRegion":"FL","postalCode":"33411","addressCountry":"US"},
          "openingHours":["Mo-Sa 08:00-23:00","Su 18:00-23:00"],
          "priceRange":"$$",
          "aggregateRating":{"@type":"AggregateRating","ratingValue":profile.reviewScore,"reviewCount":profile.reviewCount?.replace(/\D/g,'')||"148"},
          "sameAs":profile.instagram?[`https://instagram.com/${profile.instagram}`]:[],
        })}</script>
      </Helmet>
      <style>{makeCSS(a)}</style>

      {/* ── STICKY HEADER ── */}
      <header className="bp-header">
        <button className="bp-header-back" onClick={() => window.history.back()} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span className="bp-header-name"></span>
        <button className="bp-header-share" onClick={() => {
          const url = window.location.href;
          if (navigator.share) navigator.share({ title: `Book with ${profile.displayName}`, url });
          else navigator.clipboard.writeText(url);
        }} aria-label="Share">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
        </button>
      </header>

      {/* ── PROFILE SECTION — centered, luxury feel ── */}
      <div className="bp-profile">
        {/* Avatar with gold ring */}
        <div className="bp-avatar-wrap">
          <img
            src={MANE_LOGO}
            alt={profile.displayName}
            style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'30px', display:'block', transform:'scale(1.12)', transformOrigin:'center' }}
          />
        </div>

        {/* Name + Title */}
        <div className="bp-display-name">{profile.displayName}</div>
        <div className="bp-title">{profile.title}</div>
        {isFounder && <div className="bp-founder-badge">✦ Founding Member</div>}

        {/* Stats bar — single elegant line, no duplicate location */}
        <div className="bp-stats-bar">
          <span>{profile.reviewScore}★</span>
          <span className="bp-stats-sep">·</span>
          <span>{profile.reviewCount} Reviews</span>
          <span className="bp-stats-sep">·</span>
          <span>{profile.years} Years</span>
        </div>

        {profile.bio && <div className="bp-bio">{profile.bio}</div>}
        {/* Social icons row */}
        <div style={{ display:'flex', gap:'12px', alignItems:'center', marginTop:'10px' }}>
          {profile.instagram && (
            <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noopener noreferrer" style={{ display:'flex', alignItems:'center', justifyContent:'center', width:38, height:38, borderRadius:10, background:'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', flexShrink:0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.2" fill="#fff" stroke="none"/></svg>
            </a>
          )}
        </div>

      </div>

      {/* ── GOLD DIVIDER ── */}
      <div className="bp-gold-divider" />

      {/* ── SERVICES — vertical premium list ── */}
      <section ref={sectionRefs.services} id="services" style={{ paddingTop: '28px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px 4px' }}>
          <div className="bp-section-title">Services</div>
        </div>
        <div className="bp-svc-list">
          {svcs.map((s) => {
            const bc = s.badge ? (BADGE_COLORS[s.badge] || a) : null;
            return (
              <Link to={`/book?service=${s.id}`} key={s.id} className="bp-svc-card">
                <div className="bp-svc-left">
                  <div className="bp-svc-name-row">
                    <span className="bp-svc-name">{s.name}</span>
                    {s.badge && (
                      <span className="bp-svc-badge" style={{ background: `${bc}22`, color: bc, border: `1px solid ${bc}44` }}>{s.badge}</span>
                    )}
                  </div>
                  <span className="bp-svc-dur">
                    {fmtDuration(s.duration_minutes)}
                    {fmtAfterHours(s) && ` · ${fmtAfterHours(s)}`}
                  </span>
                </div>
                <div className="bp-svc-right">
                  <span className="bp-svc-price">{fmtPrice(s)}</span>
                  <span className="bp-svc-chevron">›</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── HOURS ── */}
      {profile.hoursText && (
        <section ref={sectionRefs.hours} id="hours" style={{ padding: '28px 20px 0', maxWidth: '600px', margin: '0 auto' }}>
          <div className="bp-section-title">Hours</div>
          <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '16px 18px', color: OFF, fontSize: '14px' }}>
            {profile.hoursText}
          </div>
        </section>
      )}

      {/* ── REVIEWS ── */}
      <section ref={sectionRefs.reviews} id="reviews" style={{ padding: '28px 0 0' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px' }}>
          <div className="bp-rev-header">
            <div className="bp-section-title" style={{ marginBottom: 0 }}>Reviews</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#f5c518', fontSize: '14px' }}>★★★★★</span>
              <span style={{ color: WHITE, fontWeight: 800, fontSize: '14px' }}>{profile.reviewScore}</span>
              <span style={{ color: '#7A8499', fontSize: '12px' }}>({profile.reviewCount})</span>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px' }}>
          <div ref={trackRef} className="bp-track" onScroll={() => {
            const t = trackRef.current;
            if (!t?.children[0]) return;
            setRevIdx(Math.min(Math.round(t.scrollLeft / (t.children[0].offsetWidth + 14)), REVIEWS.length-1));
          }}>
            {REVIEWS.map(r => (
              <div key={r.id} className="bp-rev-scroll">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#f5c518', fontSize: '14px', letterSpacing: '2px' }}>★★★★★</span>
                  <span style={{ fontSize: '9px', fontWeight: 800, padding: '2px 7px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', background: r.source === 'Google' ? 'rgba(66,133,244,0.1)' : `${a}18`, color: r.source === 'Google' ? '#4285f4' : a, border: r.source === 'Google' ? '1px solid rgba(66,133,244,0.2)' : `1px solid ${a}44` }}>{r.source}</span>
                </div>
                <p style={{ color: '#B8C2D0', fontSize: '14px', lineHeight: 1.7, marginTop: '12px' }}>&ldquo;{r.text}&rdquo;</p>
                <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: '12px', marginTop: '14px' }}>
                  <div style={{ color: WHITE, fontWeight: 700, fontSize: '13px' }}>{r.name}</div>
                  <div style={{ color: '#7A8499', fontSize: '11px', marginTop: '2px', display: 'flex', gap: '8px' }}>
                    {r.service && <span>{r.service}</span>}
                    {r.date && <span>{r.date}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
            {REVIEWS.map((_, i) => (
              <button key={i} className="bp-dot" onClick={() => goRev(i)} style={{ width: revIdx === i ? 20 : 6, background: revIdx === i ? a : `${a}44` }} />
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATION ── */}
      {profile.address && (
        <section ref={sectionRefs.location} id="location" style={{ padding: '28px 20px 0', maxWidth: '600px', margin: '0 auto' }}>
          <div className="bp-section-title">Location</div>
          <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
            <a href={`https://maps.google.com/?q=${encodeURIComponent(profile.address)}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 18px', textDecoration: 'none', borderBottom: profile.phone ? `1px solid ${BORDER}` : 'none' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span style={{ color: OFF, fontSize: '14px' }}>{profile.address}</span>
            </a>
            {profile.phone && (
              <a href={`tel:${profile.phone.replace(/\D/g,'')}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 18px', textDecoration: 'none' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                <span style={{ color: OFF, fontSize: '14px' }}>{phone}</span>
              </a>
            )}
          </div>
        </section>
      )}

      {/* ── POLICY ── */}
      <section style={{ padding: '28px 20px 0', maxWidth: '600px', margin: '0 auto' }}>
        <div className="bp-section-title">Cancellation Policy</div>
        {[
          { tag: 'FREE', title: '3+ hours before', desc: 'Full refund. No questions.', c: '#10b981' },
          { tag: '50%', title: 'Under 3 hours', desc: 'Half the service price charged', c: '#f59e0b' },
          { tag: '100%', title: 'No show', desc: 'Full price charged. No exceptions.', c: '#ef4444' },
        ].map((p, i) => (
          <div key={i} className="bp-policy-item" style={{ background: `${p.c}08`, border: `1px solid ${p.c}22` }}>
            <div style={{ fontWeight: 900, fontSize: '14px', color: p.c, minWidth: '40px' }}>{p.tag}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: p.c }}>{p.title}</div>
              <div style={{ color: '#7A8499', fontSize: '12px', marginTop: '2px' }}>{p.desc}</div>
            </div>
          </div>
        ))}
      </section>

      {/* ── FOOTER ── */}
      <footer className="bp-footer">
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ManeLogo size={20} showWordmark />
          <a href="/gift" style={{ color: '#6B7280', fontSize: '12px', textDecoration: 'none' }}>Gift Cards</a>
          <a href="/join-waitlist" style={{ color: '#6B7280', fontSize: '12px', textDecoration: 'none' }}>Join Waitlist</a>
          <a href="/privacy" style={{ color: '#6B7280', fontSize: '12px', textDecoration: 'none' }}>Privacy</a>
          <a href="/terms" style={{ color: '#6B7280', fontSize: '12px', textDecoration: 'none' }}>Terms</a>
          <span style={{ color: '#6B7280', fontSize: '12px' }}>© 2026 {profile.displayName}</span>
        </div>
        <div style={{ marginTop: '14px' }}>
          <Link to="/founding" className="bp-powered">Powered by <span>Mane</span> — Get your own page</Link>
        </div>
      </footer>

      {/* ── STICKY BOTTOM BAR ── */}
      <div className="bp-bottom-bar">
        <span className="bp-bottom-name">Book with {profile.displayName}</span>
        <Link to="/barber" className="bp-bottom-book">BOOK NOW →</Link>
      </div>
    </div>
  );
}
